/**
 * Header mega menu hover fix. Custom file, not part of the Horizon theme.
 *
 * Problem: Horizon opens a mega menu on `pointerenter` of the menu item and
 * closes it on `pointerleave` with no grace period. The panel is rendered a
 * few pixels below the bottom of the label and spans the full window width,
 * while the menu item is only as wide as its label. Any route from the label
 * into the panel other than straight down therefore leaves the menu item for
 * a moment, which closes the panel before the pointer arrives.
 *
 * Fix: delay the close by CLOSE_DELAY, and cancel a pending close as soon as
 * the pointer is back anywhere inside the menu, including the open panel.
 * Keyboard and focus driven closes keep the theme's original behaviour.
 *
 * `HeaderMenu.prototype.deactivate` is safe to wrap because the theme's
 * component base class resolves declarative `on:pointerleave` handlers by
 * name at call time rather than binding them on connect.
 *
 * To revert: delete this file and the line that loads it in
 * snippets/stylesheets.liquid.
 */
(() => {
  const CLOSE_DELAY = 300;

  /** @type {WeakMap<Element, ReturnType<typeof setTimeout>>} */
  const pending = new WeakMap();

  /** @param {Element} menu */
  const cancelPendingClose = (menu) => {
    const timer = pending.get(menu);

    if (timer === undefined) return;

    clearTimeout(timer);
    pending.delete(menu);
  };

  customElements.whenDefined('header-menu').then(() => {
    const prototype = customElements.get('header-menu')?.prototype;

    if (!prototype || prototype.hoverCloseDelayApplied) return;

    const close = prototype.deactivate;

    if (typeof close !== 'function') return;

    /** @param {PointerEvent | FocusEvent} event */
    prototype.deactivate = function (event) {
      if (event?.type === 'blur') {
        close.call(this, event);
        return;
      }

      cancelPendingClose(this);

      pending.set(
        this,
        setTimeout(() => {
          pending.delete(this);
          close.call(this, event);
        }, CLOSE_DELAY)
      );
    };

    prototype.hoverCloseDelayApplied = true;
  });

  document.addEventListener(
    'pointerover',
    (event) => {
      const target = event.target;

      if (!(target instanceof Element)) return;

      const menu = target.closest('header-menu');

      if (menu) cancelPendingClose(menu);
    },
    { capture: true, passive: true }
  );
})();
